/**
 * Filter Types
 */
const FilterType = Object.freeze({
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  ENUM: 'ENUM',
  DATERANGE: 'DATERANGE',
  DATETIMERANGE: 'DATETIMERANGE',
  DATE: 'DATE',
  AUTOCOMPLETE: 'AUTOCOMPLETE',
})

/**
 * Available operators for each filter type
 */
const FilterOperators = Object.freeze({
  TEXT: ['contains', 'equals', 'starts_with', 'ends_with'],
  NUMBER: ['equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'between'],
  ENUM: ['equals'],
  DATERANGE: ['equals'],
  DATETIMERANGE: ['equals'],
  DATE: ['equals'],
  AUTOCOMPLETE: ['equals']
})

/**
 * Display texts for operators (positive and negative forms)
 */
const OperatorDisplayTexts = Object.freeze({
  equals: {
    positive: 'equals',
    negative: "doesn't equal"
  },
  contains: {
    positive: 'contains',
    negative: "doesn't contain"
  },
  starts_with: {
    positive: 'starts with',
    negative: "doesn't start with"
  },
  ends_with: {
    positive: 'ends with',
    negative: "doesn't end with"
  },
  greater_than: {
    positive: 'is greater than',
    negative: 'is not greater than'
  },
  less_than: {
    positive: 'is less than',
    negative: 'is not less than'
  },
  greater_than_or_equal: {
    positive: 'is greater than or equal to',
    negative: 'is not greater than or equal to'
  },
  less_than_or_equal: {
    positive: 'is less than or equal to',
    negative: 'is not less than or equal to'
  },
  between: {
    positive: 'is between',
    negative: 'is not between'
  }
})

/**
 * @typedef {Object} FilterData
 * @property {string} apiEndpoint - API endpoint to fetch data
 * @property {string} key - property to use as a key from apiEndpoint response
 * @property {string} value - property to use as a value from apiEndpoint response
 */



class TableFilter {
  /**
   * @property {string} name - name used as a key in objects and in URL
   * @property {string} displayName - name displayed in UI
   * @property {FilterType} type - filter type
   * @property {array} enumValues - Values for filer with type of ENUM
   * @property {boolean} isInline - inline or not
   * @property {string} apiEndpoint - API endpoint for autocomplete
   * @property {FilterData} data - API endpoint for autocomplete
   * @property {boolean} integerOnly - For NUMBER type: restrict to integers only (default: false)
   * @property {boolean} allowNegative - Allow negative/NOT toggle (default: true)
   */
  constructor({ name, displayName, type, enumValues, isInline, apiEndpoint, data, integerOnly, allowNegative = true, hidden = false }) {
    this.name = name
    this.displayName = displayName
    this.isInline = isInline || false
    this.allowNegative = allowNegative
    this.hidden = hidden
    if (type) {
      if (Object.prototype.hasOwnProperty.call(FilterType, type)) {
        this.type = FilterType[type]
      } else {
        throw new Error('Wrong filter type')
      }
    }
    if (type === FilterType.ENUM) {
      this.enumValues = enumValues
    }
    if (type === FilterType.AUTOCOMPLETE) {
      this.apiEndpoint = apiEndpoint
    }
    if (type === FilterType.NUMBER) {
      this.integerOnly = integerOnly || false
    }
    this.data = data

    // Set available operators for this filter type
    this.availableOperators = FilterOperators[this.type] || ['equals']
  }
}


/**
 * FilterValue class for creating filter values to be used when passing default filters to the table
 */
class FilterValue {
  constructor({ value, operator = 'equals', negative = false }) {
    this.value = value
    this.operator = operator
    this.negative = negative
  }

  /**
   * Convert to JSON string format expected by the new filter system
   * @returns {string} JSON encoded filter clause
   */
  toJson() {
    const clause = { op: this.operator, v: this.value }
    if (this.negative) clause.not = true
    return JSON.stringify(clause)
  }

  toObject() {
    return JSON.parse(this.toJson())
  }
}

export {TableFilter, FilterType, FilterOperators, OperatorDisplayTexts, FilterValue }
